import axios from "axios";
import { apiEmailNotificationsSyncUrl } from "./apiBaseUrls";

/**
 * Subset of property fields read by the server for immediate email transitions
 * (`emails/propertyEmailSync.js` buildPropertyChangeEmailPlans, `shouldSendPeakClosingApproval`, `helpers.js`).
 * `CLOSE_OF_ESCROW` is included for Just Closed (COE date must be today).
 */
export const PROPERTY_EMAIL_NOTIFICATION_PREVIOUS_FIELDS = [
    "STATUS",
    "HSC_STATUS",
    "LIST_PRICE",
    "HSC_APPROVED",
];

export const PROPERTY_EMAIL_NOTIFICATION_CURRENT_FIELDS = [
    ...PROPERTY_EMAIL_NOTIFICATION_PREVIOUS_FIELDS,
    "SOURCE",
    "CLOSE_OF_ESCROW",
    "BUYER_AGENT_4_FIRM",
    "NAME",
    "ARCIMEDES_ROW_ID",
    "CITY",
    "STATE",
    "IS_PROFORMA_PRICING",
    "PROFORMA_CAP_RATE",
    "CAPITALIZATION_RATE",
    "POTENTIAL_FEE",
    "SOLD_PRICE",
    "CLOSE_CAP_RATE",
    "CLOSE_PRICE_PER_SQFT",
    "SQFT",
    "BUYER_NAME",
    "BUYER_FIRST_NAME",
    "BUYER_LAST_NAME",
    "BUYER_COMPANY_NAME",
    "LISTING_AGENT_1",
    "LISTING_AGENT_2",
    "LISTING_AGENT_3",
    "LISTING_AGENT_4",
    "BUYER_AGENT_1",
    "BUYER_ID",
    "HSC_PRICE",
    "NEGOTIATION_PRICE",
    "NEGOTIATION_CAP_RATE",
    "NEGOTIATION_PRICE_PER_SQFT",
    "LEASE_TYPE",
    "PRICE_PER_SQFT",
    "LOAN_TYPE",
    "LINK",
    "HSC_PRICE",
    "PURCHASE_PRICE",
    "LOAN_AMOUNT",
    "LTV",
    "INTEREST_RATE",
    "AMORTIZATION",
    "TERM",
    "CASH_ON_CASH",
    "LOAN_CLOSING_DATE",
    "TEAM_FIRST_NAME",
    "TEAM_LAST_NAME",
    "OUTSIDE_AGENT",
    "REFERRED_BY",
];

function pickFields(obj, keys) {
    if (!obj || typeof obj !== "object") return {};
    const out = {};
    for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (Object.prototype.hasOwnProperty.call(obj, k)) {
            out[k] = obj[k];
        }
    }
    return out;
}

/**
 * `pickFields` only copies keys that exist on `originalSnapshot`.
 *
 * `formSnapshot` (`this.state.data`) is the **post-edit** form, so using it to fill status-diff
 * fields (STATUS / HSC_STATUS / LIST_PRICE / HSC_APPROVED) would expose the *current* value as the
 * "previous" value and either (a) mask a real transition, or (b) manufacture a fake one when
 * `original` merely omits a key. Leave those fields missing when `original` lacks them so the
 * server's transition guards treat the previous state as unknown and do not fire emails.
 */
function mergeEmailPreviousSnapshot(originalSnapshot) {
    return originalSnapshot && typeof originalSnapshot === "object"
        ? { ...originalSnapshot }
        : {};
}

/**
 * @returns {{ previousProperty: object, currentProperty: object }}
 */
export function pickPropertyForEmailNotifications(previousProperty, currentProperty) {
    const prevMerged = mergeEmailPreviousSnapshot(previousProperty);
    return {
        previousProperty: pickFields(prevMerged, PROPERTY_EMAIL_NOTIFICATION_PREVIOUS_FIELDS),
        currentProperty: pickFields(currentProperty, PROPERTY_EMAIL_NOTIFICATION_CURRENT_FIELDS),
    };
}

/**
 * Upsert scheduled EMAIL_NOTIFICATIONS rows (4-week marketing, 2-week expiration).
 * When options.previousProperty + options.currentProperty are set, the server evaluates transitions,
 * sends matching templates immediately (including HSC closing when HSC_APPROVED flips to yes), and inserts SENT log rows (no SCHEDULE_ON).
 */
/**
 * @returns {Promise<object|null>} Parsed JSON from `/email-notifications/sync-property`, or null on failure.
 */
export function syncPropertyEmailNotifications(propertyId, options = {}) {
    if (!propertyId) return Promise.resolve(null);
    const data = { propertyId: String(propertyId) };
    if (options.previousProperty) data.previousProperty = options.previousProperty;
    if (options.currentProperty) data.currentProperty = options.currentProperty;
    if (options.agentList) data.agentList = options.agentList;
    if (options.buyerAgent4FirmValue != null) data.buyerAgent4FirmValue = options.buyerAgent4FirmValue;
    return axios({
        method: 'post',
        url: apiEmailNotificationsSyncUrl,
        headers: { Accept: '*', 'Content-Type': 'application/json' },
        data,
    })
        .then((res) => res.data)
        .catch((err) => {
            console.error('syncPropertyEmailNotifications', err && err.message ? err.message : err);
            return null;
        });
}